
import React, { useState } from 'react';
import { Wallet, Plus } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { DetailedBotAccount } from '@/types/trader';
import { CTraderAccountCard } from './CTraderAccountCard';
import { CompleteAddAccountDialog } from './CompleteAddAccountDialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useToast } from '@/hooks/use-toast';
import { useTranslation } from 'react-i18next';

interface ConnectedAccountsSectionProps {
  accounts: DetailedBotAccount[];
  isLoading: boolean;
  botId: string | undefined;
  onToggleConnection: (accountId: string, isConnected: boolean) => void;
  onDeleteAccount: (accountId: string) => void;
}

export const ConnectedAccountsSection = ({ 
  accounts, 
  isLoading, 
  botId,
  onToggleConnection,
  onDeleteAccount
}: ConnectedAccountsSectionProps) => {
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const { toast } = useToast();
  const { t } = useTranslation();
  
  const handleOpenAddDialog = () => {
    setIsAddDialogOpen(true);
  };
  
  const handleCloseAddDialog = () => {
    setIsAddDialogOpen(false);
  };

  const handleToggleConnection = async (accountId: string, isActive: boolean) => {
    onToggleConnection(accountId, isActive);
  };

  const handleDeleteAccount = async (accountId: string) => {
    onDeleteAccount(accountId);
    toast({
      title: t("account_removed"),
      description: t("account_removed_desc"),
    });
  };
  
  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Wallet size={18} />
              <CardTitle>{t('connected_accounts')}</CardTitle>
            </div>
          </div>
          <CardDescription>
            {t("connected_accounts_desc")}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-4">
              <Skeleton className="h-44 w-full" />
              <Skeleton className="h-44 w-full" />
            </div>
          ) : accounts && accounts.length > 0 ? (
            <ScrollArea className={accounts.length > 2 ? "h-[400px] pr-4" : "h-auto"}>
              <div className="space-y-3">
                {accounts.map((account) => (
                  <CTraderAccountCard
                    key={account.id}
                    account={account}
                    onToggleStatus={handleToggleConnection}
                    onDelete={handleDeleteAccount}
                  />
                ))}
              </div>
            </ScrollArea>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <p>{t('no_accounts_connected')}</p>
              <p className="text-sm mt-1">
                {t('connect_accounts_help')}
              </p>
            </div>
          )}
        </CardContent>
        <CardFooter>
          <Button 
            variant="outline" 
            className="w-full" 
            onClick={handleOpenAddDialog}
            disabled={!botId}
          >
            <Plus size={16} className="mr-2" />
            {t('add_new_account')}
          </Button>
        </CardFooter>
      </Card>
      {botId && (
        <CompleteAddAccountDialog
          isOpen={isAddDialogOpen}
          onClose={handleCloseAddDialog}
          botId={botId}
          onSuccess={() => {
            handleCloseAddDialog();
          }}
        />
      )}
    </>
  );
};
